import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as ArrowRight } from 'assets/icons/chevron.svg';
import Button from 'components/Button/Button';
import EmptyTaskManagement from 'components/EmptyTaskManagement/EmptyTaskManagement';
import Panel from 'components/Panel/Panel';
import StepItem from 'components/StepItem/StepItem';
import TitleTableWrapper from 'components/TitleTableWrapper/TitleTableWrapper';
import {
  TASK_HEAD_INDUSTRY_ANALYZE_CELLS, TASK_HEAD_INDUSTRY_CELLS,
  TASK_HEAD_INDUSTRY_MINI_ANALYZE_CELLS,
} from 'constants/dataSets';
import { BUTTON_VARIANT, DELETE_MODAL_TITLES } from 'constants/interfaces';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';

import CreateIndustry from './components/CreateIndustry/CreateIndustry';
import ItemsTable from './components/ItemsTable/ItemsTable';
import TableAnalyze from './components/TableAnalyze/TableAnalyze';

import type IMaterialityConfigIndustry from './interfaces/IMaterialityConfigIndustry';

import styles from './scss/MaterialityConfigIndustry.module.scss';

function MaterialityConfigIndustryView({
  isEmptyList, loading, onOpenModal, goBack, saveCompetitor, openModal,
  items, hasMore, onLoadMore, valueToggle, onChangeToggle,
  onDeleteItem, naceCodesData, onDeleteConfirm, isDeleteModalOpen, onCancel,
  expandedRow, setExpandedRow, itemsAnalyzed, onUpdate,
  isAffectedDataOutdated, isGlobalRisksDataOutdated,
  recordId, setCustomOffset, setOpenModal,
  onSortChange, orderBy, locationsCountries,
  isViewOnly = false,
}: IMaterialityConfigIndustry) {
  const { t } = useTranslation();

  const isRefreshDataAIEnabled = isAffectedDataOutdated;
  const isRefreshDataEnabled = isGlobalRisksDataOutdated;

  const isUpdateDisabled = Boolean(
    loading
    || isViewOnly
    || (valueToggle ? !isRefreshDataEnabled : !isRefreshDataAIEnabled),
  );
  const tooltipTitleUpdate = isUpdateDisabled ? '' : t('materialityAssessment.refreshDaGeo');

  return (
    <Panel className={styles.root}>
      <Button
        title={t('materialityAssessment.back')}
        onClick={goBack}
        variant={BUTTON_VARIANT.ghost}
        startIcon={<ArrowLeftIcon />}
      />
      <div className={styles.rootContainer}>
        <div className={styles.wrapper}>
          <TitleTableWrapper
            title={t('materialityAssessment.sectorSpecific')}
            hint={t('materialityAssessment.highImpactIndustries')}
            buttonTitle={t('materialityAssessment.addSectorSpecific')}
            onButtonClick={onOpenModal}
            buttonClassName={styles.button}
            disableButton={loading || valueToggle || isViewOnly}
            onUpdate={onUpdate}
            disableIconButton={isUpdateDisabled}
            tooltipTitleUpdate={tooltipTitleUpdate}
            titleUpdateBtn={valueToggle ? t('common.refreshData') : t('common.refreshDataAI')}
            showButton
          />
          <div className={styles.steps}>
            <StepItem title={t('materialityAssessment.sectorSpecificStep1')} step={1} done={!valueToggle} />
            <ArrowRight className={styles.arrowIcon} />
            <StepItem title={t('materialityAssessment.sectorSpecificStep2')} step={2} done={valueToggle} />
          </div>
          <div className={styles.contentWrapper}>
            {isEmptyList && !loading ? (
              <EmptyTaskManagement
                title={t('materialityAssessment.sectorSpecificForBusinessDescription')}
                checkIcon
              />
            ) : (
              <div className={styles.tableWrapper}>
                {items && items.length > 0 && !valueToggle ? (
                  <ItemsTable
                    items={items}
                    headCells={TASK_HEAD_INDUSTRY_CELLS}
                    onLoadMore={onLoadMore}
                    hasMore={hasMore}
                    isLoadingMore={loading}
                    naceCodesData={naceCodesData}
                    onDeleteItem={onDeleteItem}
                    onSortChange={onSortChange}
                    orderBy={orderBy}
                    isInfoTable
                    locationsCountries={locationsCountries}
                  />
                ) : null}
                {items && items.length > 0 && valueToggle ? (
                  <TableAnalyze
                    items={items}
                    headCells={TASK_HEAD_INDUSTRY_ANALYZE_CELLS}
                    headCellsMiniTable={TASK_HEAD_INDUSTRY_MINI_ANALYZE_CELLS}
                    onLoadMore={onLoadMore}
                    hasMore={hasMore}
                    naceCodesData={naceCodesData}
                    expandedRow={expandedRow}
                    setExpandedRow={setExpandedRow}
                    itemsAnalyzed={itemsAnalyzed}
                    onSortChange={onSortChange}
                    orderBy={orderBy}
                    locationsCountries={locationsCountries}
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={goBack}
          />
          <div className={styles.rightButtons}>
            {valueToggle ? (
              <Button
                title={t('common.prev')}
                onClick={() => onChangeToggle(false)}
                variant={BUTTON_VARIANT.cancel}
                className={styles.prevButton}
              />
            ) : null}
            {valueToggle ? (
              <Button
                title={t('common.save')}
                onClick={saveCompetitor}
                className={styles.button}
              />
            ) : (
              <Button
                title={t('common.next')}
                onClick={() => onChangeToggle(true)}
                className={styles.button}
              />
            )}
          </div>
        </div>
        <CreateIndustry
          open={openModal}
          onClose={onOpenModal}
          title={t('materialityAssessment.addSectorSpecific')}
          naceCodesData={naceCodesData}
          recordId={recordId}
          setCustomOffset={setCustomOffset}
          setOpenModal={setOpenModal}
        />
        <DeleteModal
          open={isDeleteModalOpen}
          onReject={onCancel}
          isLoading={false}
          title={DELETE_MODAL_TITLES.DELETE_SECTOR_SPECIFIC}
          hint={DELETE_MODAL_TITLES.DELETE_SECTOR_SPECIFIC_HINT}
          onConfirm={onDeleteConfirm}
        />
      </div>
    </Panel>
  );
}

export default MaterialityConfigIndustryView;
